// src/pages/player/EventDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMainPageTitle } from '../../contexts/PageContext';
import { useAuth } from '../../contexts/authContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import EventHeader from '../../components/player/events/EventHeader';
import AttendanceSection from '../../components/player/events/AttendanceSection';
import MatchDetails from '../../components/player/events/MatchDetails';
import EventMembers from '../../components/player/events/EventMembers';
import EventActivities from '../../components/player/events/EventActivities';
import { getEventStatusInfo, getEventTypeInfo, getAttendanceStatusInfo } from '../../utils/eventUtils';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1" || "http://localhost:3000/api/v1";

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [members, setMembers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);

  useEffect(() => {
    updatePageTitle(t("player.eventDetails"), t("player.viewEventDetails"));
  }, [updatePageTitle, t]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${API_URL}/event/get-event-by-id?eventId=${eventId}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Event not found");
        }
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch event details");
      }

      const data = await response.json();
      console.log("Event details:", data);
      
      // Get user's attendance status from members array
      const currentUserMember = data.members?.find(member => 
        member.userId === user?.id || 
        member.userId === user?._id || 
        member.userId === user?.metaData?.userId
      );
      
      // Add user attendance status to event object
      const eventWithAttendance = {
        ...data.event,
        userAttendanceStatus: currentUserMember?.attendanceStatus || 'pending',
        participantsCount: data.statistics?.totalMembers || data.event?.membersCount || 0
      };
      
      setEvent(eventWithAttendance);
      setMembers(data.members || []);

    } catch (err) {
      console.error("Error fetching event details:", err);
      setError(err.message || "Failed to load event details");
    } finally {
      setLoading(false);
    }
  };

  const updateAttendanceStatus = async (status, reason = '') => {
    try {
      setAttendanceLoading(true);
      
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${API_URL}/event/change-member-attendance-status`, {
        method: 'PUT',
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventId,
          memberId: user?.metaData?.userId || user?.id || user?._id,
          status: status,
          reason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update attendance");
      }

      // Refresh event details to get updated attendance
      await fetchEventDetails();
      
    } catch (err) {
      console.error("Error updating attendance:", err);
      setError(err.message || "Failed to update attendance");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${API_URL}/event/get-all-activities?eventId=${eventId}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch activities");
      }

      const data = await response.json();
      console.log("Activities data:", data);
      setActivities(data.activities || []);

    } catch (err) {
      console.error("Error fetching activities:", err);
      setActivitiesError(err.message || "Failed to load activities");
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
      fetchActivities();
    }
  }, [eventId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <LoadingSpinner message={t("player.loadingEventDetails")} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => navigate('/player/events')}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t("common.back")}
            </button>
          </div>
          <ErrorMessage message={error} onRetry={fetchEventDetails} />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("player.eventNotFound")}
            </h2>
            <button
              onClick={() => navigate('/player/events')}
              className="inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
            >
              {t("player.backToEvents")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getEventStatusInfo(event);
  const typeInfo = getEventTypeInfo(event);
  const attendanceInfo = getAttendanceStatusInfo(event);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/player/events')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t("common.back")}
          </button>
        </div>

        {/* Event Header */}
        <EventHeader 
          event={event} 
          statusInfo={statusInfo} 
          typeInfo={typeInfo} 
        />


        {/* Match Details - Only show for matches */}
        {event.eventType === 'Match' && event.team1 && event.team2 && (
          <MatchDetails 
            event={event} 
            members={members} 
            user={user} 
          />
        )}

        {/* Event Members - Only show for non-match events */}
        {members && members.length > 0 && event.eventType !== 'Match' && (
          <EventMembers 
            members={members} 
            user={user} 
          />
        )}

        {/* Event Activities */}
        <EventActivities 
          activities={activities}
          loading={activitiesLoading}
          error={activitiesError}
        />
      </div>
    </div>
  );
};

export default EventDetail; 